import { FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import useRealtime from "hooks/useRealtime";

import AirQualityCard, { AirQualityCardData } from "modules/assets/AirQualityCard";
import { BuildOptions, buildMetricCard } from "modules/assets/OverviewTab/co2";
import { buildHumidityCard, buildTemperatureCard } from "modules/assets/OverviewTab/thermal";
import ThermalCard, { ThermalCardData } from "modules/assets/ThermalCard";
import PageTemplate from "modules/common/PageTemplate";

import Card from "components/Card";

import { Breakdown } from "entities/assets";
import { AssetsApi } from "services/http";

import styles from "./style.module.scss";

const BANNER =
  "Below you can see individual sensor readings with their compliance status against DM-HSD-GU119 limits";

const METRIC_META: Record<string, { formula: string; options: BuildOptions }> = {
  co2: { formula: "CO₂", options: { shortTermLabel: "Short-term - vs outdoor level" } },
  hcho: { formula: "HCHO", options: { shortTermLabel: "Short-term: 30-min average" } },
  tvoc: {
    formula: "TVOC",
    options: {
      shortTermLabel: "Short-term: 30-min average",
      approximateInfo: "Approximate conversion (MOX sensor)",
    },
  },
  pm25: {
    formula: "PM2.5",
    options: { shortTermLabel: "Short-term: 1-hour average", longTermLabel: "Long-term: 24-hour average" },
  },
  pm10: {
    formula: "PM10",
    options: { shortTermLabel: "Short-term: 8-hour average", longTermLabel: "Long-term: 24-hour average" },
  },
};

const airBase = (formula: string, unit: string): AirQualityCardData => ({
  formula,
  unit,
  status: { tone: "gray", label: "No data" },
  realtimeLabel: "Real-time",
  realtimeValue: "",
  realtimeUnit: "",
  complianceLabel: "Limit compliance",
  metrics: [],
  updatedNote: "",
});

const thermalBase = (title: string, unit: string): ThermalCardData => ({
  title,
  unit,
  status: { tone: "gray", label: "No data" },
  realtimeLabel: "Real-time",
  realtimeValue: "",
  realtimeUnit: "",
  rangeCaption: "",
  updatedNote: "",
});

const location = (value: string | null) => value || "-";

const SensorBreakdownPage: FC = () => {
  const { id, parameter } = useParams();
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);

  const load = useCallback(() => {
    if (!id || !parameter) return;

    AssetsApi.getBreakdown(id, parameter)
      .then(({ data }) => setBreakdown(data))
      .catch(() => setBreakdown(null));
  }, [id, parameter]);

  useEffect(() => {
    load();
  }, [load]);

  useRealtime(["telemetry"], load, { assetId: Number(id) });

  const title = breakdown ? `${breakdown.short_name} - Sensor Breakdown` : "Sensor Breakdown";
  const description = breakdown
    ? breakdown.full_name
      ? `${breakdown.full_name} · ${breakdown.sensor_count} sensors`
      : `${breakdown.sensor_count} sensors`
    : "";

  const meta = parameter ? METRIC_META[parameter] : undefined;

  return (
    <PageTemplate isBackButton title={title} description={description} className={styles.page}>
      <Card className={styles.card}>
        <p className={styles.banner}>{BANNER}</p>

        <div className={styles.grid}>
          {breakdown?.kind === "metric" &&
            meta &&
            breakdown.metric_sensors.map((sensor) => {
              const built = buildMetricCard(airBase(meta.formula, sensor.card.unit), sensor.card, meta.options);
              const data: AirQualityCardData = {
                ...built,
                name: undefined,
                sensor: { id: sensor.device_eui, location: location(sensor.location) },
                footerNote: undefined,
              };
              return <AirQualityCard key={sensor.device_eui} data={data} className={styles.sensorCard} />;
            })}

          {breakdown?.kind === "thermal" &&
            breakdown.thermal_sensors.map((sensor) => {
              const built = buildTemperatureCard(thermalBase("Air temperature", sensor.card.unit), sensor.card);
              const data: ThermalCardData = {
                ...built,
                sensor: { id: sensor.device_eui, location: location(sensor.location) },
                footerNote: undefined,
              };
              return <ThermalCard key={sensor.device_eui} data={data} className={styles.sensorCard} />;
            })}

          {breakdown?.kind === "humidity" &&
            breakdown.humidity_sensors.map((sensor) => {
              const built = buildHumidityCard(thermalBase("Relative humidity", sensor.card.unit), sensor.card);
              const data: ThermalCardData = {
                ...built,
                sensor: { id: sensor.device_eui, location: location(sensor.location) },
                footerNote: undefined,
              };
              return <ThermalCard key={sensor.device_eui} data={data} className={styles.sensorCard} />;
            })}
        </div>
      </Card>
    </PageTemplate>
  );
};

export default SensorBreakdownPage;
